import { useState, useEffect } from "react";

interface HandwritingConfig {
  zoneDefaultPolicy: "always-verify" | "auto";
  heuristicEnabled: boolean;
  heuristicConfidenceThreshold: number;
  heuristicVarianceThreshold: number;
  autoEscalateToLlm: boolean;
}

interface Props {
  jobTypeId: string;
  config: HandwritingConfig;
  onSave: (config: HandwritingConfig) => void;
}

const DEFAULT_CONFIG: HandwritingConfig = {
  zoneDefaultPolicy: "always-verify",
  heuristicEnabled: true,
  heuristicConfidenceThreshold: 0.45,
  heuristicVarianceThreshold: 0.15,
  autoEscalateToLlm: false,
};

export function HandwritingSettings({ jobTypeId: _jobTypeId, config, onSave }: Props) {
  const [local, setLocal] = useState<HandwritingConfig>(config);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    setLocal({
      ...DEFAULT_CONFIG,
      ...config,
    });
  }, [config]);

  const isDirty = JSON.stringify(local) !== JSON.stringify(config);

  return (
    <div className="gt-section">
      <h3 className="rb-section-title">Handwriting Settings</h3>

      <div className="gt-field">
        <label className="gt-label" htmlFor="zone-default-policy">
          Default verification policy for handwriting zones
        </label>
        <select
          id="zone-default-policy"
          className="rb-select"
          value={local.zoneDefaultPolicy}
          onChange={(e) =>
            setLocal({ ...local, zoneDefaultPolicy: e.target.value as "always-verify" | "auto" })
          }
        >
          <option value="always-verify">Always Verify (recommended)</option>
          <option value="auto">Auto (use confidence thresholds)</option>
        </select>
        <p className="rb-hint">
          When a zone is marked as handwritten in the zone editor, fields from that zone
          will use this verification policy by default.
        </p>
      </div>

      <div className="gt-field">
        <label className="gt-toggle">
          <input
            type="checkbox"
            checked={local.heuristicEnabled}
            onChange={(e) => setLocal({ ...local, heuristicEnabled: e.target.checked })}
          />
          <span>Enable automatic handwriting detection</span>
        </label>
        <p className="rb-hint">
          When enabled, the system analyzes OCR confidence patterns to detect handwritten
          regions on documents without pre-configured zones.
        </p>
      </div>

      <div className="gt-field">
        <label className="gt-label" htmlFor="auto-escalate-llm">
          <input
            id="auto-escalate-llm"
            type="checkbox"
            checked={local.autoEscalateToLlm ?? false}
            onChange={(e) =>
              setLocal({ ...local, autoEscalateToLlm: e.target.checked })
            }
          />
          {' '}
          Auto-escalate handwriting to LLM when Tesseract confidence is low
        </label>
        <p className="rb-hint">
          Requires LLM provider to be configured on the OCR service. No effect otherwise.
        </p>
      </div>

      {local.heuristicEnabled && (
        <div className="gt-field">
          <button
            type="button"
            className="rb-btn rb-btn-cancel"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? "Hide" : "Show"} advanced thresholds
          </button>

          {showAdvanced && (
            <div style={{ marginTop: "0.75rem" }}>
              <div className="gt-slider-row">
                <span className="gt-label">Confidence threshold</span>
                <input
                  id="confidence-threshold"
                  type="range"
                  min={0.2}
                  max={0.7}
                  step={0.05}
                  value={local.heuristicConfidenceThreshold}
                  onChange={(e) =>
                    setLocal({ ...local, heuristicConfidenceThreshold: parseFloat(e.target.value) })
                  }
                />
                <span className="gt-slider-value">
                  {local.heuristicConfidenceThreshold.toFixed(2)}
                </span>
              </div>
              <p className="rb-hint">
                Fields with OCR confidence below this value are flagged as potential handwriting.
              </p>

              <div className="gt-slider-row">
                <span className="gt-label">Variance threshold</span>
                <input
                  id="variance-threshold"
                  type="range"
                  min={0.05}
                  max={0.3}
                  step={0.01}
                  value={local.heuristicVarianceThreshold}
                  onChange={(e) =>
                    setLocal({ ...local, heuristicVarianceThreshold: parseFloat(e.target.value) })
                  }
                />
                <span className="gt-slider-value">
                  {local.heuristicVarianceThreshold.toFixed(2)}
                </span>
              </div>
              <p className="rb-hint">
                High character-level confidence variance suggests handwriting rather than machine print.
              </p>
            </div>
          )}
        </div>
      )}

      {isDirty && (
        <button type="button" className="rb-btn rb-btn-save" onClick={() => onSave(local)}>
          Save Handwriting Settings
        </button>
      )}
    </div>
  );
}

export { DEFAULT_CONFIG as DEFAULT_HANDWRITING_CONFIG };
export type { HandwritingConfig };
